import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { ThemeProvider, ThemeScript } from "../components/ThemeProvider";
import { BUSINESS, activeSocialUrls, CITIES } from "../lib/site-data";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: `${BUSINESS.shortName} — SEO & Google Ads for Trades in Lehi, UT` },
      {
        name: "description",
        content: `${BUSINESS.name}: local SEO, Google Ads, and websites built for HVAC, plumbing, electrical, and home-service contractors across Utah County.`,
      },
      { name: "author", content: BUSINESS.name },
      { property: "og:site_name", content: BUSINESS.name },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { name: "theme-color", content: "#3b82f6" },
      { name: "format-detection", content: "telephone=yes" },
      { property: "og:locale", content: "en_US" },
      { name: "google-site-verification", content: "Tmq0Ya_Xq4_G_cX9_ezcEksuo2ydlP1JkKGzOdCP2ZQ" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://lehitradessaeo.com/#organization",
              name: BUSINESS.name,
              url: "https://lehitradessaeo.com",
              telephone: BUSINESS.phone,
              email: BUSINESS.email,
              address: {
                "@type": "PostalAddress",
                streetAddress: BUSINESS.address.street,
                addressLocality: BUSINESS.address.locality,
                addressRegion: BUSINESS.address.region,
                postalCode: BUSINESS.address.postalCode,
                addressCountry: BUSINESS.address.country,
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Utah County, UT" },
                ...CITIES.map((c) => ({ "@type": "City" as const, name: `${c.name}, ${c.region}` })),
              ],
              knowsAbout: [
                "Local SEO for trades contractors",
                "Google Map Pack optimization",
                "Google Ads for HVAC, plumbing, electrical, roofing",
                "Local Service Ads (LSAs)",
                "Google Business Profile management",
                "Generative Engine Optimization (GEO)",
                "AI Overviews and ChatGPT citation strategy",
              ],
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  telephone: BUSINESS.phone,
                  contactType: "sales",
                  areaServed: "US-UT",
                  availableLanguage: ["English"],
                },
              ],
              ...(activeSocialUrls().length ? { sameAs: activeSocialUrls() } : {}),
            },
            {
              "@type": "WebSite",
              "@id": "https://lehitradessaeo.com/#website",
              url: "https://lehitradessaeo.com",
              name: BUSINESS.name,
              publisher: { "@id": "https://lehitradessaeo.com/#organization" },
            },
            {
              "@type": "ProfessionalService",
              name: BUSINESS.name,
              telephone: BUSINESS.phone,
              email: BUSINESS.email,
              address: {
                "@type": "PostalAddress",
                streetAddress: BUSINESS.address.street,
                addressLocality: BUSINESS.address.locality,
                addressRegion: BUSINESS.address.region,
                postalCode: BUSINESS.address.postalCode,
                addressCountry: BUSINESS.address.country,
              },
              areaServed: "Utah County, UT",
              priceRange: "$$",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <ThemeScript />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <div className="flex min-h-screen flex-col">
          <SiteHeader />
          <div className="flex-1">
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
          </div>
          <SiteFooter />
        </div>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
